import { startTransition, useCallback, useEffect, useMemo } from "react";
import { setFocusRange } from "../../../providers/GlobalStateProvider";
import { DateRangeContent } from "../../FlightTrends/DateRange/DateRangeContent";
import { getStartAndEndDates } from "../../FlightTrends/DateRange/TrendsPageDateRangeStartAndEnd";
import {
  getMidnightTomorrowMs,
  MIN_DATE,
  ONE_DAY_OF_MS,
  INPUT_FORMAT,
} from "utils/timeUtils";
import { DateRangeStyles } from "../../FlightTrends/DateRange/DateRangeStyles";
import type { TimeRangeFinite } from "types/types";
import styled from "styled-components/macro";
import useContainerDimensions from "utils/useContainerDimensions";
import {
  useEffectOnce,
  useFocusRange,
  useFormatZoned,
  useGetPageFromPath,
  useUpdateBinSizeToBiggestAllowed,
} from "utils/customHooks/genericHooks";
import useQueryString from "utils/customHooks/useQueryString";
import { useDispatch } from "react-redux";
import { useTimeZoneMsOffset } from "utils/timeZoneHooks";
import { clamp, isEqual } from "lodash";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { atom, useAtom } from "jotai";

const CustomRangeSelectorStyles = styled.div`
  padding-top: 1em;
  .MuiFormControl-root {
    width: 176px;
  }
`;
export const focusRangeDisplayAtom = atom<TimeRangeFinite | null>(null);
const CustomRangeSelector = ({
  autoSubmit,
  onSubmit,
}: {
  autoSubmit?: boolean;
  onSubmit?: () => void;
}) => {
  const [
    {
      general: { trendsPageMaxQueryRangeDays },
    },
  ] = useFlightConfig();
  const timezoneOffsetMs = useTimeZoneMsOffset();
  const formatZoned = useFormatZoned();
  const parsedQuery = useQueryString();
  const focusRange = useFocusRange();
  const dispatch = useDispatch();
  const focusRangeFromQuery = parsedQuery.ge &&
    parsedQuery.lt && {
      ge: Number(parsedQuery.ge),
      lt: Number(parsedQuery.lt),
    };
  // if we don't get ge/lt from the query, use the focus range values instead
  const [focusRangeDisplayState, setFocusRangeDisplay] = useAtom(
    focusRangeDisplayAtom
  );
  const focusRangeDisplay =
    focusRangeDisplayState || focusRangeFromQuery || focusRange;

  // set the focusRangeDisplay once the focusRange becomes non-null
  useEffectOnce({
    callback: () => {
      setFocusRangeDisplay(focusRange);
    },
    shouldRun: Boolean(!focusRangeDisplay && focusRange),
    dependencies: [focusRange, focusRangeDisplay],
  });

  const timeZoneMsOffset = useTimeZoneMsOffset();

  // when we step forward, current lt becomes new ge
  const focusRangeDuration = useMemo(
    () => (focusRangeDisplay?.lt ?? 0) - (focusRangeDisplay?.ge ?? 0),
    [focusRangeDisplay?.lt, focusRangeDisplay?.ge]
  );
  const focusRangeOneStepForward = useMemo(() => {
    const now = Date.now();
    return {
      ge: focusRangeDisplay?.lt || now,
      lt:
        (focusRangeDisplay?.lt ?? 0) + focusRangeDuration ||
        now + focusRangeDuration,
    };
  }, [focusRangeDuration, focusRangeDisplay?.lt]);

  // when we step backward, current ge becomes new lt
  const focusRangeOneStepBackward = useMemo(
    () =>
      focusRangeDisplay
        ? {
            ge: focusRangeDisplay.ge - focusRangeDuration,
            lt: focusRangeDisplay.ge,
          }
        : focusRange,
    [focusRange, focusRangeDisplay, focusRangeDuration]
  );

  const handleClickBtnPrevious = useCallback(() => {
    setFocusRangeDisplay(focusRangeOneStepBackward);
  }, [focusRangeOneStepBackward, setFocusRangeDisplay]);
  const handleClickBtnNext = useCallback(() => {
    setFocusRangeDisplay(focusRangeOneStepForward);
  }, [focusRangeOneStepForward, setFocusRangeDisplay]);

  const handleReset = () => {
    if (focusRange) {
      setFocusRangeDisplay(focusRange);
    }
  };
  const now = Date.now();
  const midnightTomorrowMs = getMidnightTomorrowMs(now, timeZoneMsOffset);
  const minDate = formatZoned(MIN_DATE, INPUT_FORMAT);
  const maxDate = formatZoned(midnightTomorrowMs, INPUT_FORMAT);
  const { isTrendsPage } = useGetPageFromPath();
  const updateBinSizeToBiggestAllowed = useUpdateBinSizeToBiggestAllowed(true);

  const handleSubmit = useCallback(
    (newFocusRange?: TimeRangeFinite | null) => {
      onSubmit?.();
      const nextFocusRange = newFocusRange || focusRangeDisplay;
      if (!nextFocusRange || !focusRangeDisplay) {
        return;
      }
      // when we update the range, ensure the trend chart bin size is the biggest allowed to minimize number of trend bar queries
      if (isTrendsPage) {
        updateBinSizeToBiggestAllowed(nextFocusRange);
      }
      const lower = new Date(minDate).getTime();
      const upper = midnightTomorrowMs;
      const nextFocusRangeClamped = {
        ge: clamp(nextFocusRange.ge, lower, upper),
        lt: clamp(nextFocusRange.lt, lower, upper),
      };

      if (
        nextFocusRangeClamped &&
        !isEqual(nextFocusRangeClamped, focusRange)
      ) {
        startTransition(() => {
          // save modified ge & lt to url query params
          const { ge, lt } = nextFocusRangeClamped;
          const newQuery = new URLSearchParams(window.location.search);
          newQuery.set("ge", ge.toString());
          newQuery.set("lt", lt.toString());
          window.history.pushState(
            {},
            "",
            `${window.location.pathname}?${newQuery.toString()}`
          );

          dispatch(setFocusRange(nextFocusRangeClamped));
        });
      }
    },
    [
      focusRangeDisplay,
      minDate,
      midnightTomorrowMs,
      dispatch,
      focusRange,
      updateBinSizeToBiggestAllowed,
      isTrendsPage,
      onSubmit,
    ]
  );

  const handleChangeStartDate = useCallback(
    (newDate: Date) => {
      const newStartDateMs = newDate.getTime();
      if (newStartDateMs && focusRangeDisplay) {
        const lower = new Date(minDate).getTime();
        const upper = midnightTomorrowMs;
        const ge = clamp(newStartDateMs, lower, upper);
        setFocusRangeDisplay(prev => ({
          ...prev,
          ge,
          lt: prev?.lt ?? ge,
        }));
      }
    },
    [focusRangeDisplay, minDate, midnightTomorrowMs, setFocusRangeDisplay]
  );

  const handleChangeEndDate = useCallback(
    (newDate: Date) => {
      const newEndDateMs = newDate.getTime();
      if (newEndDateMs && focusRangeDisplay) {
        const lower = new Date(minDate).getTime();
        const upper = midnightTomorrowMs;
        const lt = clamp(newEndDateMs, lower, upper);
        setFocusRangeDisplay(prev => ({
          ...prev,
          ge: prev?.ge ?? lt,
          lt,
        }));
      }
    },
    [focusRangeDisplay, minDate, midnightTomorrowMs, setFocusRangeDisplay]
  );

  // if autoSubmit is enabled, submit the range when the user selects a valid range
  useEffect(() => {
    const isValidFocusRange =
      focusRangeDisplay && focusRangeDisplay.ge < focusRangeDisplay.lt;
    if (autoSubmit && isValidFocusRange) {
      handleSubmit();
    }
  }, [autoSubmit, focusRangeDisplay, handleSubmit]);

  if (!focusRangeDisplay) {
    return null;
  }

  const helperText =
    focusRangeDuration <= 0
      ? "Start date must be before End date"
      : focusRangeDuration / ONE_DAY_OF_MS > trendsPageMaxQueryRangeDays
        ? `Max range is ${trendsPageMaxQueryRangeDays.toFixed(0)} days`
        : null;

  const submitEnabled =
    !helperText &&
    !isEqual(focusRange, focusRangeDisplay) &&
    focusRangeDisplay &&
    focusRangeDisplay.lt > focusRangeDisplay.ge;

  const handleKeyDown = event => {
    if (event.key === "Enter" && submitEnabled) {
      handleSubmit();
    }
  };

  const { startDate, endDate } = getStartAndEndDates(
    focusRangeDisplay,
    true,
    formatZoned,
    timezoneOffsetMs
  );

  const cantStepBackward =
    focusRangeDisplay.ge - ONE_DAY_OF_MS < MIN_DATE.getTime();
  const cantStepForward = focusRangeDisplay.lt + ONE_DAY_OF_MS > now;

  return (
    <CustomRangeSelectorResizeHandler>
      <DateRangeContent
        cantStepBackward={cantStepBackward}
        cantStepForward={cantStepForward}
        handleClickBtnPrevious={handleClickBtnPrevious}
        startDate={startDate}
        handleChangeStartDate={handleChangeStartDate}
        endDate={endDate}
        handleChangeEndDate={handleChangeEndDate}
        handleKeyDown={handleKeyDown}
        minDate={minDate}
        maxDate={maxDate}
        handleClickBtnNext={handleClickBtnNext}
        helperText={helperText}
        submitEnabled={submitEnabled}
        handleSubmit={handleSubmit}
        handleReset={handleReset}
        autoSubmit={autoSubmit}
      />
    </CustomRangeSelectorResizeHandler>
  );
};

function CustomRangeSelectorResizeHandler({
  children,
}: {
  children: React.ReactNode;
}) {
  const [ref, dimensions] = useContainerDimensions();
  const width = dimensions.width || 0;
  const doesFitHorizontally = useMemo(() => width > 860, [width]);
  return (
    <CustomRangeSelectorStyles ref={ref}>
      <DateRangeStyles className={`${!doesFitHorizontally ? "compact" : ""}`}>
        {children}
      </DateRangeStyles>
    </CustomRangeSelectorStyles>
  );
}

export default CustomRangeSelector;
